import Help from "@mui/icons-material/Help";
import { FormControl, FormHelperText, Input, InputAdornment, InputLabel, Tooltip } from "@mui/material";
import { styled } from "@mui/material/styles";
import numeral from "numeral";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { emptyValues } from "../../../app/js/utils/constants";
import utils from "../../../app/js/utils/index";
import ErrorIcon from "./ErrorIcon";

// Styled components
const StyledFormControl = styled(FormControl)(() => ({
    marginBottom: "22px",
}));

// When reserving label/helper space, the reserved helper line supplies the row's
// vertical spacing, so the FormControl drops its own bottom margin. This keeps the
// reserve behavior self-contained and leaves extraFormControlStyles as a clean
// escape hatch layered on top so extraFormControlStyles and reserveLabelSpace do not silently fight over margin.
const StyledReservedFormControl = styled(StyledFormControl)({
    marginBottom: 0,
});

const StyledInput = styled(Input)(({ theme }) => ({
    fontSize: 14,
    padding: "4px 6px",
    marginTop: "var(--bsinput-input-mt, 22px)",
    transition: theme.transitions.create(["border-color", "box-shadow"]),
}));

const StyledFormHelperText = styled(FormHelperText)(({ isHint }) => ({
    fontStyle: "italic",
    margin: "0px",

    ...(isHint && {
        color: "dimgrey",
    }),
}));

const StyledToolTipWrapper = styled("span")({
    lineHeight: "1.5",
});

const StyledCheckbox = styled("input")({
    marginLeft: "8px",
});

const StyledLabel = styled("span")({
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    minWidth: 0,
});

const StyledInputLabel = styled(InputLabel)({
    overflow: "visible",
    display: "flex",
    alignItems: "center",
    maxWidth: "100%",
});

const StyledReservedInputLabel = styled(StyledInputLabel)({
    minHeight: "0.5em",
});

const StyledReservedHelperText = styled(StyledFormHelperText)({
    minHeight: "0.5em",
});

/** type = "number" should not be used. The built in browser behavior for an input element
 of this type includes preprocessing the value before validation with validation.js can be done.
 */
const BSInput = (props) => {
    const {
        checkbox,
        checkboxVal,
        checked,
        className,
        disableCheckbox,
        disabled,
        endAdornment,
        error,
        errorText,
        extraFormControlStyles,
        extraInputStyles,
        formcontrolClass,
        helperText,
        id,
        inputProps,
        integer,
        label,
        multiline,
        noFullWidth,
        numeric,
        onBlurValidate,
        onChange,
        onChangeCheckbox,
        onClick,
        onFocus,
        onKeyPress,
        persistHelperText,
        phone,
        placeholder,
        reserveLabelSpace,
        rows,
        startAdornment,
        startAdornmentStyles,
        toolTipText,
        type,
        value,
    } = props;

    const isNumericField = integer || numeric;
    const isEmpty = (val) => emptyValues.has(val);

    const formatValue = (val) => {
        if (isEmpty(val)) return null;

        if (type === "date") return utils.operations.formatDate("YYYY-MM-DD")(val);

        if (isNumericField) {
            if (integer) return numeral(val).format("0,000");
            return numeral(val).format("0,000.00");
        }

        if (phone) return utils.operations.formatPhoneNumber(val);

        return val;
    };

    const stripCommas = (val) => (val != null ? String(val).replace(/,/g, "") : val);
    const formatForDisplay = (val) => (isEmpty(val) ? val : formatValue(stripCommas(val)));

    const [displayValue, setDisplayValue] = useState(isNumericField ? formatForDisplay(value) : value);
    const [localError, setLocalError] = useState(false);
    // Whether the helper hint should be shown based on the last blur (or initial render).
    // Initialized from the current value so a pre-populated field hides the hint on load.
    const [showHelperOnBlur, setShowHelperOnBlur] = useState(isEmpty(value));
    const isFocusedRef = useRef(false);

    useEffect(() => {
        if (isNumericField && !isFocusedRef.current) {
            setDisplayValue(formatForDisplay(value));
        }
        // If a field is auto-populated, also check if HelperText should be displayed
        if (!isFocusedRef.current) {
            setShowHelperOnBlur(isEmpty(value));
        }
    }, [value]);

    useEffect(() => {
        if (!error) {
            setLocalError(false);
        }
    }, [error]);

    const handleOnFocus = () => {
        isFocusedRef.current = true;
        if (onFocus) onFocus();
    };

    const handleOnBlur = () => {
        isFocusedRef.current = false;
        // Re-evaluate helper hint on blur: show it only when the field is left empty.
        setShowHelperOnBlur(isEmpty(value));
        let updatedValue = value;
        if (isNumericField) {
            if (!isEmpty(value)) {
                if (numeric) {
                    const rounded = Math.round(parseFloat(stripCommas(String(value))) * 100) / 100;
                    const formatted = isNaN(rounded) ? value : formatValue(rounded);
                    setDisplayValue(formatted);
                    updatedValue = String(isNaN(rounded) ? value : rounded);
                    onChange({ target: { id, value: updatedValue } });
                } else {
                    setDisplayValue(formatForDisplay(value));
                }
            }
            if (onBlurValidate) {
                setLocalError(!!onBlurValidate(id, updatedValue));
            }
            return;
        }
        if (onBlurValidate) {
            setLocalError(!!onBlurValidate(id, formatValue(value)));
        }
        if (isEmpty(value) || !onChange) return;
        onChange({ target: { id, value: formatValue(value) } });
    };

    const handleChange = (e) => {
        if (isEmpty(e.target.value)) {
            if (isNumericField) setDisplayValue("");
            onChange({ target: { id, value: null } });
            return;
        }
        if (isNumericField) {
            const value = e.target.value.replace(/[^0-9.,]/g, "");
            const raw = stripCommas(value);
            setDisplayValue(value);
            onChange({ target: { id, value: isEmpty(raw) ? null : raw } });
            return;
        }
        onChange(e);
    };

    const FormControlStyle = formcontrolClass || (reserveLabelSpace ? StyledReservedFormControl : StyledFormControl);
    const showHelperText = () => {
        if (helperText && !hasError) {
            if (persistHelperText) {
                return helperText;
            } else if (showHelperOnBlur) {
                return helperText;
            }
        }
        return;
    };
    const hasError = error || localError;
    const helperTextContext = showHelperText();
    return (
        <FormControlStyle fullWidth={!noFullWidth} style={extraFormControlStyles}>
            {(() => {
                const LabelComponent = reserveLabelSpace ? StyledReservedInputLabel : StyledInputLabel;
                // With reserveLabelSpace the label slot always renders to hold its height,
                // even when there is no label text and no error.
                const showLabelSlot = reserveLabelSpace || label || hasError;
                if (!showLabelSlot) return null;
                return (
                    <LabelComponent shrink htmlFor={id}>
                        {label && <StyledLabel>{label}</StyledLabel>}
                        {label && checkbox && (
                            <StyledCheckbox
                                type="checkbox"
                                disabled={disableCheckbox}
                                onChange={onChangeCheckbox}
                                checked={checked}
                                value={checkboxVal}
                            />
                        )}
                        {label && toolTipText && (
                            <StyledToolTipWrapper>
                                <Tooltip title={toolTipText} placement="right-start">
                                    <span data-tooltip-id={`${id}-tool-tip`}>
                                        <Help style={{ fontSize: "110%" }} />
                                    </span>
                                </Tooltip>
                            </StyledToolTipWrapper>
                        )}
                        {hasError && <ErrorIcon />}
                    </LabelComponent>
                );
            })()}
            <StyledInput
                id={id}
                onFocus={handleOnFocus}
                value={
                    type === "date"
                        ? utils.operations.formatDate("YYYY-MM-DD")(value)
                        : isNumericField
                          ? (displayValue ?? "")
                          : value
                }
                type={type}
                placeholder={placeholder}
                onChange={handleChange}
                disableUnderline={true}
                autoComplete={"no"}
                style={hasError ? { borderColor: "red", ...extraInputStyles } : extraInputStyles}
                className={className}
                endAdornment={endAdornment && <InputAdornment position="end">{endAdornment}</InputAdornment>}
                startAdornment={
                    startAdornment && (
                        <InputAdornment position="start" style={startAdornmentStyles}>
                            {startAdornment}
                        </InputAdornment>
                    )
                }
                disabled={disabled}
                multiline={multiline}
                rows={rows}
                onKeyDown={onKeyPress}
                onBlur={handleOnBlur}
                onClick={onClick}
                inputProps={inputProps}
            />
            {(() => {
                const HelperTextComponent = reserveLabelSpace ? StyledReservedHelperText : StyledFormHelperText;
                if (errorText && hasError) {
                    return <HelperTextComponent error={true}>{errorText}</HelperTextComponent>; /* Error Text */
                }
                if (helperTextContext) {
                    return <HelperTextComponent isHint={persistHelperText}>{helperText}</HelperTextComponent>;
                }
                // Reserve an empty line so toggling helper/error text never shifts the row.
                if (reserveLabelSpace) {
                    return <StyledReservedHelperText>&nbsp;</StyledReservedHelperText>;
                }
                return null;
            })()}
        </FormControlStyle>
    );
};

BSInput.propTypes = {
    className: PropTypes.string,
    disabled: PropTypes.bool,
    endAdornment: PropTypes.node,
    error: PropTypes.bool,
    errorText: PropTypes.string,
    extraFormControlStyles: PropTypes.object,
    extraInputStyles: PropTypes.object,
    formcontrolClass: PropTypes.string,
    helperText: PropTypes.string,
    helperTextRequired: PropTypes.bool,
    id: PropTypes.string.isRequired,
    inputProps: PropTypes.object,
    label: PropTypes.string,
    multiline: PropTypes.bool,
    noFullWidth: PropTypes.bool,
    onClick: PropTypes.func,
    onFocus: PropTypes.func,
    onKeyPress: PropTypes.func,
    placeholder: PropTypes.string,
    reserveLabelSpace: PropTypes.bool,
    rows: PropTypes.number,
    startAdornment: PropTypes.node,
    startAdornmentStyles: PropTypes.object,
    toolTipText: PropTypes.string,
    type: PropTypes.string,
    value: PropTypes.any,
    onChange: PropTypes.func,
    numeric: PropTypes.bool,
    integer: PropTypes.bool,
    phone: PropTypes.bool,
    checkbox: PropTypes.bool,
    checkboxVal: PropTypes.any,
    checked: PropTypes.bool,
    disableCheckbox: PropTypes.bool,
    onChangeCheckbox: PropTypes.func,
};

export default BSInput;
